import DiscoveryFollowingTab from "../../components/DiscoveryFollowingTab";

import { AD_POSITION } from "@/constants/ads";
import { CACHEOR_KEYS } from "@/constants/cacheor";
import { SORT, SORT_ITEM_MAP } from "@/constants/discoverySort";
import useAppStore from "@/context/store";
import DiscoveryFeaturePinList from "@/pageComponents/DiscoveryPinList/feature";
import logger from "@/server/logger";
import {
  getAds,
  getCategories,
  getDiscoveryBoards,
  getDiscoveryPins,
  getDiscoveryRecommend,
} from "@/services";
import { getAiGameplayList, sortAiGameplayList } from "@/services/ai-gameplay";
import { getCalendarList, getSeoInlinks } from "@/services/qingzhou";
import { clearSearch } from "@/tracker/shared/global";
import { isSafari } from "@/utils/isSafari";
import { safeParse } from "@/utils/safeParse";
import { cacheor } from "@/utils/server/cacheor";
import { isLogined } from "@/utils/server/isLogined";
import { serverSideRedirect } from "@/utils/server/serverSide";
import { flatten, random } from "lodash";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import styles from "./index.module.less";

function randomPinPreivew(calendar) {
  const pins = flatten(
    calendar.filters.map((item) => item.children?.[0]?.preview?.pins)
  ).filter((item) => item);

  /**随机预览封面 */
  const coverPin = pins[random(0, pins.length - 1)];

  return coverPin?.file;
}

/**处理日历数据： 随机封面，过滤数据 */
function handleCalendars(data) {
  try {
    return (data || [])
      .map((item) => {
        return {
          title: item.title,
          code: item.code,
          id: item.id,
          preview_pin: randomPinPreivew(item),
          /**热度 */
          top_priority: item.top_priority,
          // 时间
          festival_time: item.festival_time || null,
          festival_end_time: item.festival_end_time || null,
          start_time: item.start_time || null,
          end_time: item.end_time || null,
          // 日期类型
          date_type: item.date_type,
        };
      })
      .filter((item) => item.preview_pin);
  } catch (error) {
    logger.error("handleCalendars error", error);
    return [];
  }
}

async function getCalendarData(query) {
  // 没有广告再请求日历信息
  const cache = await cacheor.get({ key: CACHEOR_KEYS.DISCOVERY_CALENDARS });

  if (cache && !query?._cache) {
    return handleCalendars(cache.length ? cache : []);
  } else {
    const calendarListRes = await getCalendarList(
      {
        page_num: 1,
        page_size: 20,
        load_filters: 1,
        fields: "CALENDAR",
        preview_limit: 6,
      },
      { ignoreError: true }
    );

    if (calendarListRes.data) {
      // 凌晨0点时间
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0);

      cacheor.set({
        key: CACHEOR_KEYS.DISCOVERY_CALENDARS,
        value: calendarListRes.data || [],
        ttl: Math.floor((midnight - new Date()) / 1000), // 缓存到晚上0点
      });

      return handleCalendars(
        calendarListRes.data?.length ? calendarListRes.data : []
      );
    }
  }
}

export async function getServerSideProps({ req, params, query, res }) {
  let { categoryId = null, tabId = "pins" } = params || {};

  if (tabId === "materials") {
    return serverSideRedirect(res, {
      destination: `/materials`,
      permanent: true,
    });
  }

  const cookie = req?.headers["cookie"];

  const [discoveryAds, pageConf] = await Promise.all([
    getAds({
      positions: [
        // AD_POSITION.DISCOVERY_TOP,
        AD_POSITION.DISCOVERY_WATERFALL_BANNER,
      ],
      variables: {
        category: [categoryId === "all" || !categoryId ? "none" : categoryId],
        user_tag: [tabId],
      },
      headers: {
        ...(cookie ? { cookie } : null),
      },
    }),
    // 页面配置，控制日历展示开关
    cacheor.getGrocery({
      path: "discovery-page",
      disableCache: query?._cache,
    }),
  ]);

  const [calendarList, aiGameplayList] = await Promise.all([
    pageConf?.calendarShow ? getCalendarData(query) : Promise.resolve([]),
    pageConf?.aigameplayShow ? getAiGameplayList() : Promise.resolve([]),
  ]);

  const props = {
    calendarList,
    multiColAd: discoveryAds[0],
    aiGameplayList: sortAiGameplayList(aiGameplayList),
  };

  if (pageConf.aigameplayShow)
    props.aiGameplayList = sortAiGameplayList(aiGameplayList);

  if (isLogined(req) || req.url.includes(".json")) {
    return {
      props,
    };
  }

  const { sort = 0 } = query;

  if (tabId === "pins") {
    if (+sort === 0) {
      const cachePage =
        safeParse(decodeURIComponent(req.cookies.feature_page || "{}"))?.[
          categoryId || "all"
        ] || 1;

      if (categoryId === "all") {
        categoryId = null;
      }

      const result = await getDiscoveryRecommend({
        // 随机页面，避免未登录用户刷新首屏都是重复的
        page_num: cachePage,
        page_size: 40,
        category: categoryId,
      });

      props.serversideFeaturePins = result?.pins || [];
    } else {
      const result = await getDiscoveryPins({
        category: categoryId || "",
        limit: 40,
      });

      props.serversidePins = result?.pins || [];
    }
  } else {
    const result = await getDiscoveryBoards({
      category: categoryId || "",
      limit: 40,
    });

    props.serversideBoards = result?.boards || [];
  }

  const [inlinks, result] = await Promise.all([
    getSeoInlinks({
      title: "最新采集_灵感图片大全_花瓣网",
      current_url: req.url,
    }),
    getCategories(),
  ]);

  props.inlinks = inlinks;

  let h1Desc =
    result?.categories?.find((item) => item.id === categoryId)?.name || "推荐";
  props.h1Desc = h1Desc;
  return { props };
}

export default function Discovery({
  serversideFeaturePins,
  h1Desc,
  calendarList,
  multiColAd,
  aiGameplayList,
}) {
  const router = useRouter();
  let { categoryId = null } = router.query;
  const columnNum = useAppStore((s) => s.column);

  const [clientMultiColAd, setClientMultiColAd] = useState(multiColAd);

  // 如果localStorage未设置sort就是undefined，这样就走AB的逻辑
  const sortValue = SORT_ITEM_MAP[SORT.ALL];

  const adMergeColNum = useMemo(() => {
    // 规则: 瀑布流: 4-5列, 广告位展示占2列,
    // 6-8列, 广告位展示占3列, 最多3列
    if (columnNum < 6) {
      return 2;
    }

    return 3;
  }, [columnNum]);

  const recommendAds = useMemo(() => {
    // 优先使用客户端获取的广告（Safari浏览器）
    const currentAd = clientMultiColAd;

    if (currentAd) {
      return [
        {
          ...currentAd,
          mergeCol: adMergeColNum,
          pin_id: `recommend-multiColAd-${adMergeColNum}-${
            //卡片key值更新，用于强制更新，解决safari渲染问题
            currentAd.isSafari || ""
          }`,
          type: "recommend",
        },
      ];
    }

    return [];
  }, [adMergeColNum, clientMultiColAd]);

  useEffect(() => {
    clearSearch();

    // 进入发现页面时清空 pinStack，避免返回时跳转到错误的页面
    const { clearPinStack, pinStack } = useAppStore.getState();
    if (pinStack && pinStack.length > 0) {
      clearPinStack();
    }
  }, []);

  // Safari浏览器有渲染兼容性问题，会导致广告的高度计算异常，需要在客户端强制异步更新一下
  useEffect(() => {
    if (isSafari() && multiColAd) {
      const timeout = setTimeout(() => {
        // safari浏览器标识, 用于强制更新，解决渲染问题
        setClientMultiColAd({ ...multiColAd, isSafari: "isSafari" });
      }, 50);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [multiColAd, categoryId]);

  return (
    <div
      className={styles.discovery + " calculateWrapper"}
      data-sort-type={sortValue?.label || ""}
    >
      <Head>
        <title>最新采集_灵感图片大全_花瓣网</title>
      </Head>
      {h1Desc && (
        <div className="golbalHiddenH1">
          <h1>{h1Desc}</h1>
        </div>
      )}
      <DiscoveryFollowingTab />
      <div>
        <DiscoveryFeaturePinList
          categoryId={categoryId}
          initList={serversideFeaturePins}
          sortName={sortValue?.label}
          recommends={recommendAds}
          aiGameplayList={aiGameplayList}
        />
      </div>
    </div>
  );
}
